/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { Button } from '@ui';
import { Tooltip } from '@ui/atoms/Tooltip';
import { Modal, ModalHeader, ModalBody } from '@ui/molecules/Modal';
import { Table } from '@ui/molecules/Table';
import { PDFViewer } from '@ui/molecules/PDFViewer';
import { translate } from '@utils/i18n';
import { useInspectionReports, useAddReport, useRemoveReport } from '../hooks/useInspectionReports';
const NS = 'features.proposals.attachments_step';
const t = (key, params = {}) => translate(key, Object.assign({ namespace: NS }, params));
function buildPreviewRenderer(onPreview) {
    function PreviewRenderer({ item }) {
        if (!item.pdfUrl) {
            return React.createElement("span", { "aria-hidden": "true", "data-testid": `preview-unavailable-${item.id}` });
        }
        return (React.createElement("button", { type: "button", className: "reports-brushaway__preview-btn", "aria-label": t('preview_report', { name: item.displayName }), onClick: () => onPreview(item), "data-testid": `preview-btn-${item.id}` },
            React.createElement("i", { className: "fa-light fa-paperclip", "aria-hidden": "true" })));
    }
    PreviewRenderer.displayName = 'PreviewRenderer';
    return PreviewRenderer;
}
function buildSelectRenderer(linkedIds, selectedItems, onSelectionChange) {
    function SelectRenderer({ item }) {
        const isLinked = linkedIds.has(item.id);
        const isChecked = selectedItems.includes(item.id);
        const checkbox = (React.createElement("input", { type: "checkbox", "data-testid": `report-checkbox-${item.id}`, checked: isChecked, onChange: (e) => onSelectionChange(item.id, e.target.checked), "aria-label": t('select_named', { name: item.displayName }) }));
        if (isLinked) {
            return (React.createElement(Tooltip, { content: t('report_linked_tooltip'), position: "top" }, checkbox));
        }
        return checkbox;
    }
    SelectRenderer.displayName = 'SelectRenderer';
    return SelectRenderer;
}
// ── Component ─────────────────────────────────────────────────────────────────
export function ReportsBrushaway({ proposalId, inspectionReportsUrl, addReportUrl, removeReportUrl, open, onClose }) {
    const [pendingAdd, setPendingAdd] = useState(new Set());
    const [previewReport, setPreviewReport] = useState(null);
    useEffect(() => {
        if (open)
            setPendingAdd(new Set());
    }, [open]);
    const { data: reports = [], isLoading } = useInspectionReports(proposalId, inspectionReportsUrl, open);
    const addReport = useAddReport(proposalId, addReportUrl);
    const removeReport = useRemoveReport(proposalId, removeReportUrl);
    const linkedIds = useMemo(() => new Set(reports.filter((r) => r.linked).map((r) => r.id)), [reports]);
    const selectedItems = useMemo(() => [...linkedIds, ...pendingAdd], [linkedIds, pendingAdd]);
    const handleSelectionChange = useCallback((id, checked) => {
        if (linkedIds.has(id)) {
            if (!checked)
                removeReport.mutate(id);
            return;
        }
        setPendingAdd((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            }
            else {
                next.delete(id);
            }
            return next;
        });
    }, [linkedIds, removeReport]);
    const handleSelectAll = useCallback((selectAll) => {
        if (selectAll) {
            const nonLinked = reports.filter((r) => !r.linked).map((r) => r.id);
            setPendingAdd(new Set(nonLinked));
        }
        else {
            setPendingAdd(new Set());
        }
    }, [reports]);
    const handleAddSelected = () => {
        const ids = [...pendingAdd];
        void Promise.all(ids.map((id) => addReport.mutateAsync(id)))
            .then(() => {
            onClose();
        })
            .catch(() => undefined);
    };
    const handlePreview = useCallback((report) => {
        setPreviewReport(report);
    }, []);
    const pendingCount = pendingAdd.size;
    const selectableCount = reports.filter((r) => !r.linked).length;
    const allPendingSelected = selectableCount > 0 && pendingCount === selectableCount;
    const columns = useMemo(() => [
        {
            dataKey: 'select',
            label: (React.createElement("input", { type: "checkbox", "data-testid": "reports-select-all", checked: allPendingSelected, disabled: selectableCount === 0, onChange: (e) => handleSelectAll(e.target.checked), "aria-label": t('select_named', { name: t('inspection_reports') }) })),
            width: '40px',
            columnType: 'action',
            renderer: buildSelectRenderer(linkedIds, selectedItems, handleSelectionChange)
        },
        {
            dataKey: 'preview',
            label: t('col_preview'),
            width: '48px',
            columnType: 'action',
            renderer: buildPreviewRenderer(handlePreview)
        },
        { dataKey: 'inspectionDate', label: t('col_inspection_date'), width: '14%' },
        { dataKey: 'date', label: t('col_date'), width: '10%' },
        { dataKey: 'buildingName', label: t('col_building'), width: '20%' },
        { dataKey: 'systemAsset', label: t('col_system_asset'), width: '20%' },
        { dataKey: 'displayName', label: t('col_display_name') }
    ], [
        allPendingSelected,
        selectableCount,
        handleSelectAll,
        handlePreview,
        linkedIds,
        selectedItems,
        handleSelectionChange
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Brushaway, { controlledOpen: open, onClose: onClose, minWidth: 860, closeOnClickOutside: true },
            React.createElement(BrushawayHeader, { title: t('inspection_reports') }),
            React.createElement(BrushawayBody, null,
                React.createElement(Table, { data: reports, columns: columns, variant: "detail", getItemId: (item) => item.id, isLoading: isLoading, enableCustomScrollbar: true })),
            React.createElement(BrushawayFooter, null,
                React.createElement(Button, { variant: "primary", disabled: pendingCount === 0, onClick: handleAddSelected, "data-testid": "add-to-proposal-btn" }, t('add_to_proposal_count', { count: pendingCount })),
                React.createElement(Button, { variant: "secondary", onClick: onClose }, t('close')))),
        (previewReport === null || previewReport === void 0 ? void 0 : previewReport.pdfUrl) && (React.createElement(Modal, { isOpen: true, onClose: () => setPreviewReport(null), ariaLabel: t('preview_report', { name: previewReport.displayName }), className: "qmb-ui-modal--document" },
            React.createElement(ModalHeader, { title: previewReport.displayName, onClose: () => setPreviewReport(null) }),
            React.createElement(ModalBody, null,
                React.createElement(PDFViewer, { file: previewReport.pdfUrl }))))));
}
